/**
 * perfWatch — 주기적 임계 샘플링(저fps 지속 / 저메모리 임박) 경고 순수 코어. Phaser/DOM 의존 0.
 *
 * idleblade 포팅: Unity `Application.lowMemory`(OS 신호 → GC 트리거) + `LogCollector` 메모리 스냅샷
 * (`Profiler.GetTotalAllocatedMemory` MB)을 웹으로. 웹은 lowMemory API가 미흡하고 JS 수동 GC도 불가해
 * `performance.memory` heap/limit 비율로 임박을 추정해 **경고 로그만** 낸다(주기 N초마다 1회).
 * 시간은 dtMs 누적(performance.now 미사용 → 결정론).
 */

export type PerfWarnKind = "lowFps" | "highHeap";

export interface PerfWarnContext {
  kind: PerfWarnKind;
  /** lowFps면 fps 값, highHeap이면 heap/limit 비율(0~1). */
  value: number;
  fps: number;
  heapMB: number | null;
  heapLimitMB: number | null;
}

export interface PerfWatchSample {
  fps: number;
  heapMB: number | null;
  heapLimitMB: number | null;
}

export interface PerfWatchOptions {
  /** 평가 주기 ms (기본 5000). */
  sampleMs?: number;
  /** 저fps 경고 임계 (기본 40 — 이 미만이면 경고). */
  lowFps?: number;
  /** 저메모리 경고 임계 비율 heap/limit (기본 0.85). */
  highHeapRatio?: number;
  /** 임계 위반 시 호출 — 앱이 console.warn/diag-log로 출력. */
  onWarn: (ctx: PerfWarnContext) => void;
}

export interface PerfWatch {
  /** 매 프레임 호출 — dtMs 누적, sampleMs마다 1회 임계 평가. */
  tick(dtMs: number, sample: PerfWatchSample): void;
}

export function createPerfWatch(opts: PerfWatchOptions): PerfWatch {
  const sampleMs = opts.sampleMs ?? 5000;
  const lowFps = opts.lowFps ?? 40;
  const ratio = opts.highHeapRatio ?? 0.85;
  let acc = 0;

  function tick(dtMs: number, sample: PerfWatchSample): void {
    acc += dtMs;
    if (acc < sampleMs) return;
    acc = 0;

    if (sample.fps < lowFps) {
      opts.onWarn({ kind: "lowFps", value: sample.fps, ...sample });
    }
    if (sample.heapMB !== null && sample.heapLimitMB !== null && sample.heapLimitMB > 0) {
      const r = sample.heapMB / sample.heapLimitMB;
      if (r > ratio) opts.onWarn({ kind: "highHeap", value: r, ...sample });
    }
  }

  return { tick };
}
